import { type TextProps } from '@loadsmart/miranda-react'

import { formatDate, parseISO } from '../../utils/date'
import { getTimeFromMilitaryTime } from '../../utils/militaryTime'
import type { StopAppointment, AppointmentSlot } from '../domain/Appointment'

function formatTime(time: string) {
  const { hours, minutes } = getTimeFromMilitaryTime(time)
  return `${hours}:${minutes}`
}

function formatAppointmentTimeByType(timeObject: AppointmentSlot) {
  return {
    type: timeObject.type,
    start: formatTime(timeObject.start),
    end: formatTime(timeObject.end),
  }
}

function formatAppointmentDateByType(dateObject: AppointmentSlot, format = 'MMM dd') {
  const dateStart = parseISO(dateObject.start)
  const dateEnd = parseISO(dateObject.end)

  if (dateStart && dateEnd) {
    return {
      type: dateObject.type,
      start: formatDate(dateStart, format),
      end: formatDate(dateEnd, format),
    }
  }

  return {}
}

function formatAppointmentTimeAsText(
  appointment?: Pick<StopAppointment, 'scheduledTime' | 'suggestedTime'>
) {
  if (!appointment) {
    return null
  }

  if (appointment.scheduledTime) {
    const { type, start, end } = formatAppointmentTimeByType(appointment.scheduledTime)
    const time = type === 'before' ? end : start
    const betweenTime = type === 'between' ? `until ${end}` : ''
    return `${time} ${betweenTime}`
  }
  if (appointment.suggestedTime) {
    const { type, start, end } = formatAppointmentTimeByType(appointment.suggestedTime)
    const time = type === 'before' ? end : start
    const betweenTime = type === 'between' ? `until ${end}` : ''
    return `${time} ${betweenTime}`
  }
}

export function formatAppointmentDate(
  appointment?: Pick<StopAppointment, 'scheduledDate' | 'suggestedDate'>,
  format?: string
) {
  if (!appointment) {
    return null
  }
  if (appointment.scheduledDate) {
    const { type, start, end } = formatAppointmentDateByType(appointment.scheduledDate, format)
    return type === 'at' ? start : `${start} - ${end}`
  }
  if (appointment.suggestedDate) {
    const { type, start, end } = formatAppointmentDateByType(appointment.suggestedDate, format)
    return type === 'at' ? start : `${start} - ${end}`
  }
}

export function formatAppointmentDescription(
  appointment?: Pick<StopAppointment, 'scheduledTime' | 'suggestedTime'>
) {
  if (!appointment) {
    return null
  }

  const timeText = formatAppointmentTimeAsText(appointment)

  if (appointment.scheduledTime) {
    const { type } = formatAppointmentTimeByType(appointment.scheduledTime)
    return `Arrive ${type} ${timeText}`
  }
  if (appointment.suggestedTime) {
    const { type } = formatAppointmentTimeByType(appointment.suggestedTime)
    return `Available ${type} ${timeText}`
  }
}

export function formatAppointmentStatus(status: string) {
  if (status === 'pending') {
    return 'Pending Schedule'
  }
  if (status === 'requested') {
    return 'Required Schedule'
  }
  return 'Scheduled'
}

export function formatAppointmentStatusColor(status: string): TextProps['color'] {
  if (status === 'pending') {
    return 'color-danger-60'
  }

  if (status === 'confirmed') {
    return 'color-success-60'
  }

  return 'color-warning-60'
}

export function formatFullAppointment(
  appointment?: Pick<
    StopAppointment,
    'status' | 'scheduledTime' | 'suggestedTime' | 'scheduledDate' | 'suggestedDate'
  >
) {
  if (!appointment) {
    return undefined
  }

  const status = formatAppointmentStatus(appointment.status)
  const time = formatAppointmentTimeAsText(appointment)
  const date = formatAppointmentDate(appointment)

  return `${date} • ${status} ${time}`
}
